import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Lightbox from './Lightbox';
import Card from '../common/Card';
import Button from '../common/Button';
import { formatCategory, formatDate } from '../../utils/formatters';
import { FaArrowLeft, FaArrowRight, FaQuoteLeft } from 'react-icons/fa';

const ProjectDetail = ({ project, prevProject, nextProject }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Combine cover image and additional images for gallery
  const allImages = [project.coverImage, ...(project.images || [])];

  return (
    <div className="pt-24 md:pt-28 pb-16 bg-white min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8 md:mb-12 max-w-7xl mx-auto">
          <Link to="/portfolio">
            <Button
              variant="ghost"
              size="sm"
              className="text-text-secondary hover:text-accent transition-colors duration-300 -ml-2"
            >
              <FaArrowLeft className="inline mr-2" />
              Back to Portfolio
            </Button>
          </Link>
        </div>

        {/* Project Header */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-16">
          <div className="mb-4">
            <span className="text-accent font-body font-semibold uppercase tracking-wider text-sm">
              {formatCategory(project.category)}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
            {project.title}
          </h1>
          <div className="flex flex-wrap gap-6 text-text-secondary font-body text-base">
            {project.clientName && (
              <div>
                <span className="font-semibold text-text-primary">Client:</span> {project.clientName}
              </div>
            )}
            {project.date && (
              <div>
                <span className="font-semibold text-text-primary">Date:</span> {formatDate(project.date)}
              </div>
            )}
          </div>
        </div>

        {/* Main Image */}
        <div className="max-w-7xl mx-auto mb-16 md:mb-20">
          <div
            className="relative overflow-hidden rounded-xl shadow-soft-lg cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <img
              src={project.coverImage}
              alt={project.title}
              className="w-full h-auto group-hover:scale-105 transition-all duration-600"
              loading="eager"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-400 flex items-center justify-center">
              <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-black/50 backdrop-blur-sm px-6 py-3 rounded-full">
                <span className="text-sm font-medium">Click to view full size</span>
              </div>
            </div>
          </div>
        </div>

        {/* Project Description */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="p-8">
            <h2 className="text-2xl font-heading font-semibold text-secondary mb-4">
              About This Project
            </h2>
            <p className="text-text-primary font-body leading-relaxed whitespace-pre-wrap">
              {project.description}
            </p>
          </Card>
        </div>

        {/* Process Section */}
        {project.process && (
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="p-8">
              <h2 className="text-2xl font-heading font-semibold text-secondary mb-4">
                The Process
              </h2>
              <p className="text-text-primary font-body leading-relaxed whitespace-pre-wrap">
                {project.process}
              </p>
            </Card>
          </div>
        )}

        {/* Image Gallery */}
        {project.images && project.images.length > 0 && (
          <div className="max-w-7xl mx-auto mb-16 md:mb-20">
            <h2 className="text-3xl md:text-h2 font-heading font-bold text-secondary mb-8 md:mb-12">
              Gallery
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {project.images.map((image, index) => (
                <div
                  key={index}
                  className="relative overflow-hidden rounded-lg shadow-soft cursor-pointer group aspect-4/5"
                  onClick={() => openLightbox(index + 1)}
                >
                  <img
                    src={image}
                    alt={`${project.title} ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-600"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end justify-center pb-6">
                    <span className="text-white font-medium text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      View Image
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results Section */}
        {project.results && (
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="p-8">
              <h2 className="text-2xl font-heading font-semibold text-secondary mb-4">
                Results & Outcome
              </h2>
              <p className="text-text-primary font-body leading-relaxed whitespace-pre-wrap">
                {project.results}
              </p>
            </Card>
          </div>
        )}

        {/* Testimonial */}
        {project.testimonial && project.testimonial.text && (
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="p-8 bg-gradient-to-br from-primary to-white">
              <FaQuoteLeft className="text-accent text-3xl mb-4 opacity-50" />
              <blockquote className="text-xl font-body text-text-primary italic mb-4">
                "{project.testimonial.text}"
              </blockquote>
              {project.testimonial.author && (
                <p className="font-heading font-semibold text-secondary">
                  — {project.testimonial.author}
                </p>
              )}
            </Card>
          </div>
        )}

        {/* Navigation to Other Projects */}
        {(prevProject || nextProject) && (
          <div className="max-w-4xl mx-auto mt-16 pt-8 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                {prevProject && (
                  <Link to={`/portfolio/${prevProject.slug}`}>
                    <Button variant="outline">
                      <FaArrowLeft className="inline mr-2" />
                      Previous Project
                    </Button>
                  </Link>
                )}
              </div>
              <div>
                {nextProject && (
                  <Link to={`/portfolio/${nextProject.slug}`}>
                    <Button variant="outline">
                      Next Project
                      <FaArrowRight className="inline ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
